import { useState, useEffect, useCallback } from "react";
import { Users, Plus, Search, Calendar, ArrowRight } from "lucide-react";
import { Button, Modal, Pagination, Form, Tag, Tooltip } from "antd";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  listPortalCommunities,
  createPortalCommunity,
} from "services/communities.service";
import { Link } from "react-router-dom";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { buildCommunityPayload } from "./commons/community.helpers";
import CommunityForm from "Components/CommunityForm/CommunityForm";
import useCommunityMasterData from "hooks/useCommunityMasterData/useCommunityMasterData";
import PageLoader from "Components/PageLoader/PageLoader";

const PAGE_SIZE = 10;

function CommunitiesList() {
  const { userData } = useAuthStore();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [locations, setLocations] = useState([{ city_code: "", area_codes: [] }]);
  const [showTC, setShowTC] = useState(false);
  const masterData = useCommunityMasterData();

  const loadCommunities = useCallback(async (pageNum) => {
    setLoading(true);
    const { data, errors } = await listPortalCommunities({
      current_page: pageNum,
      page_size: PAGE_SIZE,
      include_statistics: true,
    });
    setLoading(false);
    if (errors?.length > 0) {
      renderErrorNotifications(errors);
      return;
    }
    setItems(data?.data ?? []);
    setPagination(
      data?.meta?.pagination ?? {
        current_page: pageNum,
        total: 0,
        total_pages: 0,
      }
    );
  }, []);

  useEffect(() => {
    loadCommunities(page);
  }, [loadCommunities, page]);

  const q = search.trim().toLowerCase();
  const filtered = !q
    ? items
    : items.filter((c) =>
        [c.name, c.display_name, c.description, ...(c.collection_tags ?? [])]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );

  const openCreateModal = () => {
    form.resetFields();
    setLocations([{ city_code: "", area_codes: [] }]);
    setShowTC(false);
    form.setFieldsValue({ owner_metadata: [{ name: "" }] });
    setIsCreateOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateOpen(false);
    setShowTC(false);
    form.resetFields();
    setLocations([{ city_code: "", area_codes: [] }]);
  };

  const handleCreate = async () => {
    let values;
    try {
      values = await form.validateFields({ recursive: true });
    } catch {
      setTimeout(() => {
        const el = document.querySelector(".ant-form-item-has-error");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }

    const payload = buildCommunityPayload(values, locations);

    if (payload.operational_locations.length === 0) {
      renderErrorNotifications([
        {
          message:
            "Add at least one location with a city and at least one area.",
        },
      ]);
      return;
    }

    setCreating(true);
    const { errors } = await createPortalCommunity({
      ...payload,
      audit: { created_by: userData.email },
    });
    setCreating(false);

    if (errors?.length > 0) {
      renderErrorNotifications(errors);
      return;
    }

    closeCreateModal();
    renderSuccessNotifications({
      title: "Community created",
      message: "Your new community is ready.",
    });
    if (page === 1) {
      loadCommunities(1);
    } else {
      setPage(1); // useEffect will reload
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Communities</h1>
          <p className="text-muted-foreground text-sm">
            Manage your organization&apos;s communities and members. You can
            create up to 5 communities by default.
          </p>
        </div>
        <Tooltip
          title={
            items.length >= 5
              ? "Maximum 5 communities allowed per organization"
              : null
          }
        >
          <Button
            onClick={openCreateModal}
            icon={<Plus className="mr-2 h-4 w-4" />}
            disabled={items.length >= 5 || loading}
          >
            Create Community
          </Button>
        </Tooltip>

        <Modal
          open={isCreateOpen}
          onCancel={closeCreateModal}
          title="Create new community"
          width={680}
          className="modal-no-scrollbar"
          styles={{
            body: { maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" },
          }}
          footer={[
            <Button key="cancel" onClick={closeCreateModal}>
              Cancel
            </Button>,
            <Button
              key="create"
              type="primary"
              loading={creating}
              onClick={handleCreate}
            >
              Create community
            </Button>,
          ]}
        >
          <CommunityForm
            form={form}
            locations={locations}
            setLocations={setLocations}
            masterData={masterData}
            mode="create"
            showTC={showTC}
            setShowTC={setShowTC}
          />
        </Modal>
      </div>

      <div className="flex items-center space-x-2 bg-card p-4 rounded-2xl border border-border/50">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, description…"
            className="h-10 w-full rounded-xl border border-border/50 bg-background pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-accent/20 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <PageLoader showHero={false} showTabs={false} showCards={false} />
      ) : filtered.length === 0 && search ? (
        <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border border-dashed bg-card">
          <p className="text-sm font-medium">No matches</p>
          <p className="text-xs text-muted-foreground mt-1">
            Try a different search term.
          </p>
        </div>
      ) : filtered.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c) => (
              <div
                key={c.community_id}
                className="group rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card hover:shadow-elevated transition-all"
              >
                <div className="aspect-[2/1] bg-muted relative">
                  {c.cover_url ? (
                    <img
                      src={c.cover_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src="https://media.licdn.com/dms/image/v2/C5612AQEr3WmxhGSbxg/article-cover_image-shrink_720_1280/article-cover_image-shrink_720_1280/0/1644862734630?e=2147483647&v=beta&t=M5AxxQvXksyGDUNYiIyZ7G0zAhfqdmYgrlQhMdTkLfI"
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-2 right-2">
                    {c.verification_status === "REJECTED" ? (
                      <Tooltip
                        title={c.reason_for_rejection || "No reason provided"}
                        placement="bottomRight"
                      >
                        <Tag
                          color="red"
                          className="cursor-help m-0 text-[10px] font-bold uppercase"
                        >
                          {c.verification_status}
                        </Tag>
                      </Tooltip>
                    ) : (
                      <Tag
                        color={
                          c.verification_status === "VERIFIED"
                            ? "green"
                            : c.verification_status === "PENDING"
                              ? "orange"
                              : "default"
                        }
                        className="m-0 text-[10px] font-bold uppercase"
                      >
                        {c.verification_status}
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="space-y-1">
                    <h3 className="font-bold text-lg">
                      {c.display_name || c.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]" style={{ whiteSpace: "pre-wrap" }}>
                      {c.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-muted-foreground gap-1">
                      <Users className="h-4 w-4 shrink-0" />
                      <span>Members: {c.total_members}</span>
                    </div>
                    <span className="text-xs bg-muted px-2 py-1 rounded font-medium max-w-[50%] truncate">
                      {c.activities?.join(", ")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 shrink-0" />
                      Events: {c.total_events}
                    </span>
                    <span className="flex items-center gap-1">
                      {c.is_active ? (
                        <Tag color="green">Active</Tag>
                      ) : (
                        <Tag color="red">Inactive</Tag>
                      )}
                    </span>
                  </div>
                  <div className="pt-2 flex space-x-2">
                    <Link
                      className="flex-1"
                      to={`/shoup-community/communities/${c.community_id}`}
                    >
                      <Button
                        type="default"
                        className="w-full"
                        icon={<ArrowRight className="h-4 w-4" />}
                      >
                        Manage
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {(pagination.total_pages ?? 0) > 1 && (
            <div className="flex justify-center pt-4">
              <Pagination
                current={pagination.current_page ?? page}
                pageSize={PAGE_SIZE}
                total={pagination.total ?? 0}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-4 bg-card rounded-2xl border border-dashed">
          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-coral-100 to-coral-50 flex items-center justify-center">
            <Users className="h-10 w-10 text-gray-700" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold">No communities yet</h3>
            <p className="text-muted-foreground max-w-sm mx-auto text-sm">
              Create your first community to start building and managing your
              audience.
            </p>
          </div>
          <Button
            size="large"
            onClick={openCreateModal}
            icon={<Plus className="mr-2 h-4 w-4" />}
          >
            Create your first community
          </Button>
        </div>
      )}
    </div>
  );
}

export { CommunitiesList as default };
